import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormContextParts } from 'components/forms/form/Form.types';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import {
  DbsSettings,
  Tracking,
  UpdateDbsSettingsPayload,
} from 'models/DbsSettings';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { DBS_SETTINGS_BASE_QUERY_KEY } from '../useFetchDbsSettings';
import useUpdateDbsSettingsMutation from '../useUpdateDbsSettingsMutation';
import { normalizeSocialUrl } from './socialUrl';
import {
  SUPPORTED_SOCIAL_PLATFORMS,
  SupportedSocialPlatform,
  TRACKING_FIELDS,
  TrackingField,
  WEBSITE_SETTINGS_TEXT_FIELDS,
  WebsiteSettingsFormValues,
} from './WebsiteSettingsTab.schema';

const getPropertyUids = (options?: ({ value?: string } | undefined)[]) =>
  (options ?? []).map((option) => option?.value);

const useWebsiteSettingsFormSubmit = (dbsSettings?: DbsSettings) => {
  const { agencyUid } = useAppUser();
  const { notifyError, notifySuccess } = useNotify();
  const { mutateAsync: updateDbsSettings } = useUpdateDbsSettingsMutation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return async (
    formValues: WebsiteSettingsFormValues,
    _event: unknown,
    {
      formState: { dirtyFields },
      reset,
    }: FormContextParts<WebsiteSettingsFormValues>,
  ) => {
    const payload: UpdateDbsSettingsPayload = {};

    WEBSITE_SETTINGS_TEXT_FIELDS.forEach((field) => {
      if (dirtyFields[field]) {
        // The endpoint clears a value on explicit null, so an emptied field must not send ''.
        payload[field] = formValues[field].trim() || null;
      }
    });

    if (dirtyFields.widgetSettings) {
      payload.widgetSettings = formValues.widgetSettings;
    }

    if (dirtyFields.tracking) {
      const dirtyTracking = dirtyFields.tracking;

      // The endpoint merges the tracking object, so an id left out keeps its stored value.
      payload.tracking = Object.fromEntries(
        TRACKING_FIELDS.filter((field) => dirtyTracking[field]).map((field) => [
          field,
          formValues.tracking[field].trim() || null,
        ]),
      ) as Partial<Tracking>;
    }

    const featuredPropertyUids = getPropertyUids(formValues.featuredProperties);
    if (
      featuredPropertyUids.join(',') !==
      (dbsSettings?.featuredPropertyUids ?? []).join(',')
    ) {
      payload.featuredPropertyUids = featuredPropertyUids as string[];
    }

    if (dirtyFields.socialLinks && dbsSettings) {
      const dirtySocialLinks = dirtyFields.socialLinks;
      const unmanaged = dbsSettings.socialLinks.filter(
        ({ type }) =>
          !SUPPORTED_SOCIAL_PLATFORMS.some((platform) => platform === type),
      );

      payload.socialLinks = [
        ...unmanaged,
        ...SUPPORTED_SOCIAL_PLATFORMS.map((type) => ({
          type,
          url: normalizeSocialUrl(
            dirtySocialLinks[type]
              ? formValues.socialLinks[type]
              : dbsSettings.socialLinks.find((link) => link.type === type)
                  ?.url ?? '',
          ),
        })).filter(({ url }) => !!url),
      ];
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    try {
      await updateDbsSettings({ agencyUid: agencyUid!, payload });

      await queryClient.invalidateQueries([
        DBS_SETTINGS_BASE_QUERY_KEY,
        agencyUid,
      ]);

      reset({
        ...formValues,
        tracking: Object.fromEntries(
          TRACKING_FIELDS.map((field) => [
            field,
            formValues.tracking[field].trim(),
          ]),
        ) as Record<TrackingField, string>,
        socialLinks: Object.fromEntries(
          SUPPORTED_SOCIAL_PLATFORMS.map((platform) => [
            platform,
            normalizeSocialUrl(formValues.socialLinks[platform]),
          ]),
        ) as Record<SupportedSocialPlatform, string>,
      });
      notifySuccess(t('pageDirectBookingEngine.websiteSettings.updateSuccess'));
    } catch (error: any) {
      notifyError(
        getErrorMessage({
          ...error?.response?.data,
          baseKey: 'pageDirectBookingEngine.apiErrorCodes.',
          t,
        }),
      );
    }
  };
};

export default useWebsiteSettingsFormSubmit;
